class FabricSwatchPanel extends HTMLElement {
  constructor() {
    super();
    const overlay = this.querySelector('.fabric-swatch-panel__overlay');
    if (overlay) {
      overlay.addEventListener('click', () => {
        if (this.isWithinOpenGracePeriod()) return;
        this.close();
      });
    }

    this.boundOnDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.boundOnDocumentClick = this.onDocumentClick.bind(this);

    this.querySelectorAll('.fabric-swatch-panel__image img').forEach((img) => {
      if (img.complete) img.classList.add('is-loaded');
    });

    this.expanded = false;
    this.summaryToggle = this.querySelector('.fabric-swatch-panel__summary');
    if (this.summaryToggle) {
      this.summaryToggle.addEventListener('click', this.toggleSummary.bind(this));
      this.summaryToggle.addEventListener('keydown', (evt) => {
        if (evt.code !== 'Enter' && evt.code !== 'Space') return;
        evt.preventDefault();
        this.toggleSummary();
      });
    }

    this.drawer = this.querySelector('.fabric-swatch-panel__drawer');
    this.body = this.querySelector('.fabric-swatch-panel__body');
    this.header = this.querySelector('.fabric-swatch-panel__header');
    this.inner = this.querySelector('.fabric-swatch-panel__inner');
    this.footer = this.querySelector('.fabric-swatch-panel__footer');
    this.footerRow = this.querySelector('.fabric-swatch-panel__footer-row');
    this.viewCartLink = this.querySelector('.fabric-swatch-panel__view-cart');
    this.activeCard = null;
    this.max = parseInt(this.dataset.max, 10) || 10;
    this.selectedCount = this.querySelectorAll('.fabric-swatch-panel__card.is-added').length;
    this.counterEl = this.querySelector('.fabric-swatch-panel__selected-count');
    this.limitBannerEl = this.querySelector('.fabric-swatch-panel__limit-banner');
    this.selectedItemsEl = this.querySelector('.fabric-swatch-panel__selected-items');
    this.removeLabel = this.dataset.removeLabel || 'Remove';

    if (this.selectedItemsEl) {
      this.selectedItemsEl.addEventListener('click', (evt) => {
        const removeButton = evt.target.closest('.fabric-swatch-panel__selected-item-remove');
        if (!removeButton) return;
        this.removeSwatch(removeButton.dataset.variantId, removeButton);
      });
    }

    window.addEventListener('resize', this.updateDrawerOffset.bind(this));
    window.addEventListener('resize', this.updateSelectedItemsMaxHeight.bind(this));

    this.updateDrawerOffset();

    if (this.footer && typeof ResizeObserver !== 'undefined') {
      this.footerResizeObserver = new ResizeObserver(() => this.updateDrawerOffset());
      this.footerResizeObserver.observe(this.footer);
    }

    this.body.addEventListener('click', (evt) => {
      const card = evt.target.closest('.fabric-swatch-panel__card');
      if (card) {
        this.collapseSummary();
        this.openDrawer(card);
        return;
      }
      this.closeDrawer();
    });

    const drawerClose = this.querySelector('.fabric-swatch-panel__drawer-close');
    if (drawerClose) drawerClose.addEventListener('click', this.closeDrawer.bind(this));

    this.messageEl = this.drawer.querySelector('.fabric-swatch-panel__drawer-message');
    this.ctaEl = this.drawer.querySelector('.fabric-swatch-panel__drawer-cta');
    this.spinnerEl = this.drawer.querySelector('.loading__spinner');
    if (this.ctaEl) this.ctaEl.addEventListener('click', this.handleCtaClick.bind(this));

    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      this.close();
      this.refreshCartIconBubble();
    });
  }

  refreshCartIconBubble() {
    fetch(`${window.location.pathname}?sections=cart-icon-bubble`)
      .then((response) => response.json())
      .then((sectionsResponse) => {
        const cartIconBubbleHtml = sectionsResponse && sectionsResponse['cart-icon-bubble'];
        if (!cartIconBubbleHtml) return;
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble) cartIconBubble.innerHTML = this.getSectionInnerHTML(cartIconBubbleHtml);
      })
      .catch(() => {
        // silently continue — bubble keeps its last known state
      });
  }

  syncWithCart() {
    return Promise.all([
      fetch('/cart.js').then((response) => response.json()),
      fetch(`${window.location.pathname}?sections=cart-metafields`)
        .then((response) => response.json())
        .catch(() => ({})),
    ])
      .then(([cart, sectionsResponse]) => {
        const swatchMetafields = this.parseSwatchMetafields(sectionsResponse && sectionsResponse['cart-metafields']);
        const swatchItems = cart.items.filter((item) => swatchMetafields.has(item.key));
        const swatchVariantIds = new Set(swatchItems.map((item) => String(item.id)));

        this.selectedCount = swatchItems.reduce((total, item) => total + item.quantity, 0);

        this.querySelectorAll('.fabric-swatch-panel__card').forEach((card) => {
          const variantId = card.dataset.variantId;
          card.classList.toggle('is-added', Boolean(variantId) && swatchVariantIds.has(variantId));
        });

        this.updateCounter();
        this.renderSelectedItems(swatchItems, swatchMetafields);
      })
      .catch(() => {
        // silently continue — cards start unmarked
      });
  }

  parseSwatchMetafields(rawSectionHtml) {
    const metafieldsByKey = new Map();
    if (!rawSectionHtml) return metafieldsByKey;

    try {
      const json = this.getSectionInnerHTML(rawSectionHtml);
      const data = JSON.parse(json);
      (data.items || []).forEach((entry) => {
        if (entry.is_fabric_swatch) metafieldsByKey.set(entry.key, { name: entry.name });
      });
    } catch {
      // ignore malformed/missing section response — falls back to properties-only detection
    }

    return metafieldsByKey;
  }

  renderSelectedItems(swatchItems, swatchMetafields) {
    if (!this.selectedItemsEl) return;

    this.selectedItemsEl.innerHTML = '';

    swatchItems.forEach((item) => {
      const variantId = String(item.id);
      const metafields = swatchMetafields && swatchMetafields.get(item.key);
      const name = metafields && metafields.name;
      this.selectedItemsEl.appendChild(this.buildSelectedItemRow(variantId, name, item.quantity));
    });

    this.updateDrawerOffset();
  }

  buildSelectedItemRow(variantId, name, quantity = 1) {
    const row = document.createElement('div');
    row.className = 'fabric-swatch-panel__selected-item';
    row.dataset.variantId = variantId;
    row.dataset.quantity = quantity;
    
    // Store swatch_sku for removal analytics fallback (spec §3.G)
    const card = this.querySelector(`.fabric-swatch-panel__card[data-variant-id="${variantId}"]`);
    if (card && card.dataset.swatchSku) {
      row.dataset.swatchSku = card.dataset.swatchSku;
    }

    const nameEl = document.createElement('span');
    nameEl.className = 'fabric-swatch-panel__selected-item-name';
    nameEl.textContent = name || '';
    row.appendChild(nameEl);

    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'fabric-swatch-panel__selected-item-remove';
    removeButton.dataset.variantId = variantId;

    const removeTextEl = document.createElement('span');
    removeTextEl.textContent = this.removeLabel;
    removeButton.appendChild(removeTextEl);

    if (this.spinnerEl) {
      const spinnerClone = this.spinnerEl.cloneNode(true);
      spinnerClone.classList.add('hidden');
      removeButton.appendChild(spinnerClone);
    }

    row.appendChild(removeButton);

    return row;
  }

  addSelectedItemRow(variantId, name) {
    if (!this.selectedItemsEl || !variantId) return;
    if (this.selectedItemsEl.querySelector(`.fabric-swatch-panel__selected-item[data-variant-id="${variantId}"]`)) {
      return;
    }
    this.selectedItemsEl.appendChild(this.buildSelectedItemRow(variantId, name));
    this.updateDrawerOffset();
  }

  removeSwatch(variantId, button) {
    if (!variantId || (button && button.disabled)) return;
    if (button) button.disabled = true;

    const row = this.selectedItemsEl
      ? this.selectedItemsEl.querySelector(`.fabric-swatch-panel__selected-item[data-variant-id="${variantId}"]`)
      : null;
    const removedQuantity = row ? parseInt(row.dataset.quantity, 10) || 1 : 1;

    const spinner = button ? button.querySelector('.loading__spinner') : null;
    if (button) button.classList.add('loading');
    if (spinner) spinner.classList.remove('hidden');

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 0,
        sections: ['cart-icon-bubble', 'cart-metafields'],
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json())
      .then((cart) => {
        const card = this.querySelector(`.fabric-swatch-panel__card[data-variant-id="${variantId}"]`);
        if (card) card.classList.remove('is-added');

        if (row) row.remove();

        this.selectedCount = Math.max(0, this.selectedCount - removedQuantity);
        this.updateCounter();
        this.updateDrawerOffset();

        const cartIconBubbleHtml = cart.sections && cart.sections['cart-icon-bubble'];
        if (cartIconBubbleHtml) {
          const cartIconBubble = document.getElementById('cart-icon-bubble');
          if (cartIconBubble) cartIconBubble.innerHTML = this.getSectionInnerHTML(cartIconBubbleHtml);
        }

        // eslint-disable-next-line no-undef
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'fabric-swatch-panel',
          cartData: cart,
          productVariantId: variantId,
        });
      })
      .catch((error) => {
        console.error('[fabric-swatch-panel] remove swatch failed:', error);
      })
      .finally(() => {
        if (button) {
          button.disabled = false;
          button.classList.remove('loading');
        }
        if (spinner) spinner.classList.add('hidden');
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  updateCounter() {
    if (this.counterEl) this.counterEl.textContent = `${this.selectedCount} of ${this.max} selected`;
    if (this.limitBannerEl) this.limitBannerEl.hidden = this.selectedCount < this.max;
  }

  showMessage(text, autoDismissMs) {
    if (!this.messageEl) return;
    clearTimeout(this.messageTimeout);
    this.messageEl.textContent = text;
    this.messageEl.hidden = false;
    if (autoDismissMs) {
      this.messageTimeout = setTimeout(() => this.hideMessage(), autoDismissMs);
    }
  }

  hideMessage() {
    if (!this.messageEl) return;
    clearTimeout(this.messageTimeout);
    this.messageEl.hidden = true;
  }

  handleCtaClick() {
    if (!this.ctaEl) return;

    if (this.ctaEl.classList.contains('is-disabled')) {
      this.showMessage(this.ctaEl.dataset.disabledMessage || this.ctaEl.textContent.trim());
      return;
    }

    const variantId = this.ctaEl.dataset.variantId;
    if (!variantId) return;

    // Set flag for AEP pixel to detect fabric sample ATC (§3.F)
    window._swatchAddInProgress = true;
    
    // Store fabric name AND swatch_sku for analytics (spec §3.F triple fallback)
    if (this.activeCard) {
      const fabricName = this.activeCard.dataset.fabricName || '';
      const swatchSku = this.activeCard.dataset.swatchSku || '';
      
      if (fabricName) {
        sessionStorage.setItem('fabric-sample-atc-name', fabricName);
      }
      if (swatchSku) {
        sessionStorage.setItem('fabric-sample-atc-sku', swatchSku);
      }
    }

    this.ctaEl.setAttribute('aria-disabled', true);
    this.ctaEl.classList.add('loading');
    if (this.spinnerEl) this.spinnerEl.classList.remove('hidden');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
          },
        ],
        sections: ['cart-metafields'],
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data.description || data.message || 'Failed to add swatch to cart');
        if (this.activeCard) this.activeCard.classList.add('is-added');
        this.selectedCount += 1;
        this.updateCounter();
        this.addSelectedItemRow(variantId, this.activeCard ? this.activeCard.dataset.fabricName : '');
        this.refreshCartIconBubble();
        this.closeDrawer();
      })
      .catch((error) => {
        console.error('[fabric-swatch-panel] add to cart failed:', error);
      })
      .finally(() => {
        window._swatchAddInProgress = false;
        sessionStorage.removeItem('fabric-sample-atc-name');
        sessionStorage.removeItem('fabric-sample-atc-sku');  // Also remove SKU
        this.ctaEl.removeAttribute('aria-disabled');
        this.ctaEl.classList.remove('loading');
        if (this.spinnerEl) this.spinnerEl.classList.add('hidden');
      });
  }

  openDrawer(card) {
    if (this.activeCard && this.activeCard !== card) {
      this.activeCard.classList.remove('is-active');
    }
    this.activeCard = card;
    card.classList.add('is-active');

    const fabricName = card.dataset.fabricName || '';
    const composition = card.dataset.composition || '';
    const featureTags = card.dataset.featureTags || '';
    const care = card.dataset.care || '';
    const hoverImage = card.dataset.hoverImage || '';
    const variantId = card.dataset.variantId || '';

    const image = this.drawer.querySelector('.fabric-swatch-panel__drawer-image');
    const imageEl = image.querySelector('img');
    const nameEl = this.drawer.querySelector('.fabric-swatch-panel__drawer-name');
    const compositionEl = this.drawer.querySelector('.fabric-swatch-panel__drawer-composition');
    const featuresEl = this.drawer.querySelector('.fabric-swatch-panel__drawer-features');
    const careEl = this.drawer.querySelector('.fabric-swatch-panel__drawer-care');
    const ctaEl = this.drawer.querySelector('.fabric-swatch-panel__drawer-cta');

    if (hoverImage) {
      image.classList.remove('fabric-swatch-panel__drawer-image--empty');
      imageEl.hidden = false;
      imageEl.src = hoverImage;
      imageEl.alt = fabricName;
    } else {
      image.classList.add('fabric-swatch-panel__drawer-image--empty');
      imageEl.hidden = true;
      imageEl.removeAttribute('src');
      imageEl.alt = '';
    }

    this.setDrawerText(nameEl, fabricName);
    this.setDrawerText(compositionEl, composition);
    this.setDrawerText(careEl, care);

    featuresEl.innerHTML = '';
    const tags = this.parseFeatureTags(featureTags);
    if (tags.length) {
      tags.forEach((tag) => {
        const pill = document.createElement('span');
        pill.className = 'fabric-swatch-panel__drawer-feature-pill';
        pill.textContent = tag;
        featuresEl.appendChild(pill);
      });
      featuresEl.hidden = false;
    } else {
      featuresEl.hidden = true;
    }

    if (variantId) {
      ctaEl.dataset.variantId = variantId;
      ctaEl.hidden = false;
    } else {
      delete ctaEl.dataset.variantId;
      ctaEl.hidden = true;
    }

    this.hideMessage();
    const ctaTextEl = ctaEl.querySelector('.fabric-swatch-panel__drawer-cta-text');
    if (ctaTextEl) ctaTextEl.textContent = ctaEl.dataset.addText;
    ctaEl.setAttribute('aria-disabled', 'false');
    if (card.classList.contains('is-unavailable')) {
      ctaEl.classList.add('is-disabled');
      ctaEl.setAttribute('aria-disabled', 'true');
      ctaEl.dataset.disabledMessage = ctaEl.dataset.unavailableText;
    } else if (card.classList.contains('is-added')) {
      ctaEl.classList.add('is-disabled');
      ctaEl.setAttribute('aria-disabled', 'true');
      ctaEl.dataset.disabledMessage = ctaEl.dataset.alreadyAddedText;
    } else if (this.selectedCount >= this.max) {
      ctaEl.classList.add('is-disabled');
      ctaEl.setAttribute('aria-disabled', 'true');
      ctaEl.dataset.disabledMessage = ctaEl.dataset.limitReachedText;
    } else {
      ctaEl.classList.remove('is-disabled');
      delete ctaEl.dataset.disabledMessage;
    }

    this.updateDrawerOffset();
    this.drawer.classList.add('is-open');
    this.setFooterInert(true);
  }

  setFooterInert(isInert) {
    if (!this.footer) return;
    [this.selectedItemsEl, this.viewCartLink].forEach((el) => {
      if (!el) return;
      el.style.pointerEvents = isInert ? 'none' : '';
      if (isInert) {
        el.setAttribute('inert', '');
      } else {
        el.removeAttribute('inert');
      }
    });
  }

  updateDrawerOffset() {
    if (!this.footer) return;
    this.style.setProperty('--fsp-drawer-bottom', `${this.footer.offsetHeight}px`);
  }

  parseFeatureTags(featureTags) {
    try {
      const parsed = JSON.parse(featureTags);
      if (Array.isArray(parsed)) {
        return parsed.map((tag) => String(tag).trim()).filter(Boolean);
      }
    } catch {
      // not JSON — fall through to delimiter split
    }

    return featureTags
      .split('•')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  setDrawerText(element, value) {
    if (value) {
      element.textContent = value;
      element.hidden = false;
    } else {
      element.textContent = '';
      element.hidden = true;
    }
  }

  closeDrawer() {
    if (this.drawer) this.drawer.classList.remove('is-open');
    if (this.activeCard) this.activeCard.classList.remove('is-active');
    this.activeCard = null;
    this.setFooterInert(false);
  }

  toggleSummary() {
    const expanding = !this.expanded;
    this.setSummaryExpanded(expanding);
    if (expanding) this.closeDrawer();
  }

  collapseSummary() {
    this.setSummaryExpanded(false);
  }

  setSummaryExpanded(expanded) {
    this.expanded = expanded;

    if (this.summaryToggle) {
      this.summaryToggle.setAttribute('aria-expanded', String(this.expanded));
      this.summaryToggle.classList.toggle('is-expanded', this.expanded);
    }
    if (this.selectedItemsEl) {
      if (this.expanded) this.updateSelectedItemsMaxHeight();
      this.selectedItemsEl.classList.toggle('is-open', this.expanded);
    }
    this.updateDrawerOffset();
  }

  updateSelectedItemsMaxHeight() {
    if (!this.selectedItemsEl || !this.inner || !this.header || !this.footer) return;
    const available = this.inner.offsetHeight - this.header.offsetHeight - this.footer.offsetHeight;
    this.style.setProperty('--fsp-selected-items-max', `${Math.max(available, 0)}px`);
  }

  onDocumentKeydown(evt) {
    if (evt.code === 'Escape') this.close();
  }

  onDocumentClick(evt) {
    if (evt.target.closest('.fabric-swatch-panel__inner') || evt.target.closest('[data-open-fabric-swatch-panel]')) {
      return;
    }
    if (this.isWithinOpenGracePeriod()) return;
    this.close();
  }

  isWithinOpenGracePeriod() {
    return typeof this.openedAt === 'number' && Date.now() - this.openedAt < 350;
  }

  open(triggeredBy) {
    this.openedAt = Date.now();
    this.syncWithCart();

    if (triggeredBy) this.setActiveElement(triggeredBy);

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.querySelector('.fabric-swatch-panel__inner');
        const focusElement = this.querySelector('.fabric-swatch-panel__close') || containerToTrapFocusOn;
        // eslint-disable-next-line no-undef
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.addEventListener('keydown', this.boundOnDocumentKeydown);
    document.addEventListener('click', this.boundOnDocumentClick);
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    this.collapseSummary();
    // eslint-disable-next-line no-undef
    removeTrapFocus(this.activeElement);
    document.removeEventListener('keydown', this.boundOnDocumentKeydown);
    document.removeEventListener('click', this.boundOnDocumentClick);
    document.body.classList.remove('overflow-hidden');
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('fabric-swatch-panel', FabricSwatchPanel);

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-open-fabric-swatch-panel]');
  if (!trigger) return;

  const panel = document.querySelector('fabric-swatch-panel');
  if (panel) panel.open(trigger);
});
